import { SCHEMA } from '@vedalib/editor/lib/brand'
import React, { useCallback, useMemo } from 'react'

import Padding from 'components/Editor/Toolbar/Padding'
import { LayoutScroll } from 'components/LayoutPage'
import {
  FieldColorPicker,
  FieldNumber,
  FieldRadio,
  FieldSegmented,
  FieldSlider,
  FormField,
} from 'components/formKit'
import { Typography } from 'components/uiKit/Typography'
import { BrandButtonFont, BrandFieldShadow } from 'services/Branding/components'
import { BORDER_OPTIONS, HORIZONTAL_ALIGN_OPTIONS } from 'services/Branding/constants'
import {
  useBrandColorOptions,
  useShowBorder,
  useThemeValueGetter,
} from 'services/Branding/fieldConfigs/useFieldConfigs'
import { genSchema, genBrandField } from 'services/Branding/genSchema'
import { useElementFontCss, useElementFontValues, useUpdateSchema } from 'services/Branding/hooks'
import { t } from 'services/Translation'

import type { DividerFormBrandType } from './dividerTypes'

export const STYLE_FORM_SCHEMA = genSchema(SCHEMA.divider.style, {
  line: {
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.color'),
      useConfigs: [useBrandColorOptions],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 10, showInput: true },
    }),
  },
  numbered: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    justifyContent: genBrandField({
      as: FieldRadio<'flex-start' | 'center' | 'flex-end'>,
      label: t('input.label.justifyContent'),
      props: {
        options: HORIZONTAL_ALIGN_OPTIONS,
        hideRadio: true,
        fluid: false,
        direction: 'horizontal',
      },
    }),
    height: genBrandField({
      as: FieldNumber,
      label: t('input.label.height'),
      rules: [{ min: 1, message: ' ' }],
      props: { width: 64 },
    }),
    width: genBrandField({
      as: FieldNumber,
      label: t('input.label.width'),
      rules: [{ min: 1, message: ' ' }],
      props: { width: 64 },
    }),
    borderRadius: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderRadius'),
      props: { min: 0, max: 100, showInput: true },
      useConfigs: [useThemeValueGetter],
    }),
    borderStyle: genBrandField({
      as: FieldSegmented<'none' | 'solid'>,
      label: t('input.label.border'),
      props: { options: BORDER_OPTIONS },
      useConfigs: [useThemeValueGetter],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder],
    }),
  },
  effects: {
    boxShadow: genBrandField({
      as: BrandFieldShadow,
      label: t('elements.divider.schema.boxShadow'),
      useConfigs: [useThemeValueGetter],
    }),
  },
  double: {
    marginTop: genBrandField({
      as: FieldNumber,
      label: t('elements.divider.schema.interval'),
      rules: [{ min: 1, max: 20, required: true, message: ' ' }],
      props: { width: 64 },
    }),
  },
})

const DividerFormStyle: DividerFormBrandType = ({
  device,
  subConfigs,
  element,
  edited,
  setForm,
}) => {
  const { font } = element || {}
  const path = useMemo(() => ['style', device], [device])
  const schema = useUpdateSchema(path, subConfigs, STYLE_FORM_SCHEMA)

  const values = useElementFontValues('divider', device, font)
  const styles = useElementFontCss('divider', device, font)

  const handleSetTypography = useCallback(() => setForm?.('base'), [setForm])

  return (
    <LayoutScroll sizeAutoCapable>
      <Padding bottom gap sides>
        <Typography weight='medium'>{t('elements.divider.tags.line')}</Typography>
        <FormField config={schema.line.borderColor} />
        <FormField config={schema.line.borderWidth} />

        <Typography weight='medium'>{t('elements.divider.tags.numbering')}</Typography>
        <FormField config={schema.numbered.backgroundColor} />
        <FormField config={schema.numbered.justifyContent} />
        <FormField config={schema.numbered.height} />
        <FormField config={schema.numbered.width} />
        <FormField config={schema.numbered.borderRadius} />
        <FormField config={schema.numbered.borderStyle} />
        <FormField config={schema.numbered.borderColor} />
        <FormField config={schema.numbered.borderWidth} />

        <Typography weight='medium'>{t('style.effects')}</Typography>
        <FormField config={schema.effects.boxShadow} />

        <Typography weight='medium'>{t('elements.divider.tags.double')}</Typography>
        <FormField config={schema.double.marginTop} />

        <BrandButtonFont
          edited={edited?.font?.base}
          font='base'
          onClick={handleSetTypography}
          styles={styles.base}
          values={values.base}
        >
          {t('page.brand.tab.typography')}
        </BrandButtonFont>
      </Padding>
    </LayoutScroll>
  )
}

export default React.memo(DividerFormStyle) as typeof DividerFormStyle
